import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@cibli/shared';
import * as faceapi from 'face-api.js';
import { Check, Move, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface CropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: string | null;
  onCrop: (croppedBase64: string) => void;
  onCancel: () => void;
}

const CropDialog: React.FC<CropDialogProps> = ({ open, onOpenChange, image, onCrop, onCancel }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Detect face and reposition when dialog is opened
  useEffect(() => {
    if (!open) return;
    setTimeout(async () => {
      if (!imgRef.current) return;

      // Load tiny face detector model
      await faceapi.loadTinyFaceDetectorModel('/face-api');

      try {
        // Detect face
        const detection = await faceapi.detectSingleFace(
          imgRef.current,
          new faceapi.TinyFaceDetectorOptions(),
        );
        if (!detection) return;

        // Resize detection
        const resizedDetection = faceapi.resizeResults(detection, imgRef.current);

        // Set crop
        const imgWidth = imgRef.current.width;
        const imgHeight = imgRef.current.height;
        const { x, y, width, height } = resizedDetection.box;
        const size = Math.min(Math.min(width, height) * 2, Math.min(imgWidth, imgHeight));

        const newCrop: PixelCrop = {
          unit: 'px',
          width: size,
          height: size,
          x: Math.max(0, Math.min(x + width / 2 - size / 2, imgWidth - size)),
          y: Math.max(0, Math.min(y + height / 2 - size / 2, imgHeight - size)),
        };

        setCrop(newCrop);
        setCompletedCrop(newCrop);
        return detection;
      } catch (error) {
        console.error('Error detecting face:', error);
      }
    }, 100);
  }, [open]);

  const handleApplyCrop = () => {
    if (completedCrop && imgRef.current) {
      const canvas = document.createElement('canvas');
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      const scaleFactor = 2; // 2x plus grande
      canvas.width = completedCrop.width * scaleFactor;
      canvas.height = completedCrop.height * scaleFactor;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          imgRef.current,
          completedCrop.x * scaleX,
          completedCrop.y * scaleY,
          completedCrop.width * scaleX,
          completedCrop.height * scaleY,
          0,
          0,
          completedCrop.width * scaleFactor,
          completedCrop.height * scaleFactor,
        );
        const base64Image = canvas.toDataURL('image/webp', 1);
        onCrop(base64Image);
        onOpenChange(false);
      }
    }
  };

  // Ensure crop is properly initialized when image loads
  const handleImgLoad: React.ReactEventHandler<HTMLImageElement> = e => {
    const target = e.target as HTMLImageElement;
    const width = target.width;
    const height = target.height;
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    const newCrop: PixelCrop = {
      unit: 'px',
      width: size,
      height: size,
      x,
      y,
    };
    setCrop(newCrop);
    setCompletedCrop(newCrop);
  };

  const handleCancel = () => {
    onOpenChange(false);
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[999999]" overlayClassName="z-[999998]">
        <DialogHeader>
          <DialogTitle>{t('Forms.PersonalInfo.cropDialog.title')}</DialogTitle>
          <DialogDescription>{t('Forms.PersonalInfo.cropDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center">
          <div className="mb-4 relative">
            {image && (
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={c => setCompletedCrop(c)}
                aspect={1}
                circularCrop
                minWidth={50}
                minHeight={50}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={image}
                  alt={t('Forms.PersonalInfo.cropDialog.capturedImage')}
                  className="max-w-full max-h-[50vh]"
                  onLoad={handleImgLoad}
                />
              </ReactCrop>
            )}
            <div className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-md">
              <Move className="h-5 w-5" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            {t('Forms.PersonalInfo.cropDialog.instructions')}
          </p>
          <div className="flex justify-between w-full">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleApplyCrop}
              className="bg-primary text-white hover:bg-primary/90"
            >
              <Check className="h-4 w-4 mr-2" />
              {t('Forms.PersonalInfo.cropDialog.submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CropDialog;
